import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/client'

interface MandantAntwort {
  id: number
  kuerzel: string
  name: string
  strasse: string | null
  plz: string | null
  ort: string | null
  genehmigungsinhaber: string | null
  betriebsleiter: string | null
  ansprechpartner: string | null
  ansprechpartnerEmail: string | null
  ansprechpartnerTelefon: string | null
  aktiv: boolean
  anzahlBenutzer: number
}

interface AnlageErgebnis {
  mandant: MandantAntwort
  adminEmail: string
  einladungVersendet: boolean
  hinweis: string | null
  einladungslink: string | null
}

interface MandantDetail {
  mandant: MandantAntwort
  anzahlKurse: number
  anzahlFahrzeuge: number
  anzahlFahrer: number
}

const LEER = {
  kuerzel: '',
  name: '',
  strasse: '',
  plz: '',
  ort: '',
  genehmigungsinhaber: '',
  betriebsleiter: '',
  ansprechpartner: '',
  ansprechpartnerEmail: '',
  ansprechpartnerTelefon: '',
}

export function Mandanten() {
  const [mandanten, setMandanten] = useState<MandantAntwort[]>([])
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [formular, setFormular] = useState<typeof LEER | null>(null)
  const [bearbeiten, setBearbeiten] = useState<MandantAntwort | null>(null)
  const [detail, setDetail] = useState<MandantDetail | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [einladung, setEinladung] = useState<AnlageErgebnis | null>(null)

  async function laden() {
    setLaedt(true)
    try {
      setMandanten(await api.get<MandantAntwort[]>('/api/mandanten'))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function anlegen(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!formular) return
    setSpeichert(true)
    setMeldung(null)
    try {
      const ergebnis = await api.post<AnlageErgebnis>('/api/mandanten', {
        kuerzel: formular.kuerzel,
        name: formular.name,
        strasse: formular.strasse || null,
        plz: formular.plz || null,
        ort: formular.ort || null,
        genehmigungsinhaber: formular.genehmigungsinhaber || null,
        betriebsleiter: formular.betriebsleiter || null,
        ansprechpartner: formular.ansprechpartner,
        ansprechpartnerEmail: formular.ansprechpartnerEmail,
        ansprechpartnerTelefon: formular.ansprechpartnerTelefon || null,
        aktiv: true,
      })
      if (ergebnis.einladungVersendet) {
        setMeldung({
          art: 'erfolg',
          text: `Mandant „${ergebnis.mandant.name}" angelegt. Einladung an ${ergebnis.adminEmail} versendet.`,
        })
      } else {
        setEinladung(ergebnis)
        setMeldung({
          art: 'hinweis',
          text: `Mandant angelegt, aber keine Mail versendet: ${ergebnis.hinweis ?? 'unbekannter Grund'}`,
        })
      }
      setFormular(null)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Anlegen fehlgeschlagen' })
    } finally {
      setSpeichert(false)
    }
  }

  async function speichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!bearbeiten) return
    setSpeichert(true)
    setMeldung(null)
    try {
      await api.put(`/api/mandanten/${bearbeiten.id}`, {
        kuerzel: bearbeiten.kuerzel,
        name: bearbeiten.name,
        strasse: bearbeiten.strasse,
        plz: bearbeiten.plz,
        ort: bearbeiten.ort,
        genehmigungsinhaber: bearbeiten.genehmigungsinhaber,
        betriebsleiter: bearbeiten.betriebsleiter,
        ansprechpartner: bearbeiten.ansprechpartner,
        ansprechpartnerEmail: bearbeiten.ansprechpartnerEmail,
        ansprechpartnerTelefon: bearbeiten.ansprechpartnerTelefon,
        aktiv: bearbeiten.aktiv,
      })
      setMeldung({ art: 'erfolg', text: 'Änderungen gespeichert' })
      setBearbeiten(null)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen' })
    } finally {
      setSpeichert(false)
    }
  }

  async function zeigeDetail(id: number) {
    if (detail?.mandant.id === id) {
      setDetail(null)
      return
    }
    try {
      setDetail(await api.get<MandantDetail>(`/api/mandanten/${id}`))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Mandanten</h1>
          <p>Busunternehmen auf dieser Plattform. Jeder Mandant hat eigene Kurse, Fahrzeuge und Mitarbeiter.</p>
        </div>
        {!formular && !bearbeiten && (
          <button className="knopf-akzent" onClick={() => setFormular({ ...LEER })}>
            Mandant anlegen
          </button>
        )}
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {/* Ohne Mailversand bleibt der Link sonst unerreichbar — er existiert nur einmal. */}
      {einladung?.einladungslink && (
        <div className="karte abschnitt" style={{ borderLeft: '4px solid var(--orange-500)' }}>
          <h2>Einladungslink für {einladung.adminEmail}</h2>
          <p style={{ color: 'var(--grau-700)', fontSize: '0.88rem' }}>
            Es wurde keine E-Mail versendet. Geben Sie diesen Link an den Ansprechpartner weiter —
            er wird nicht erneut angezeigt. Über <strong>Zugänge → Einladung erneut senden</strong>
            lässt sich später ein neuer erzeugen.
          </p>
          <div className="protokoll">
            {einladung.einladungslink}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button
              className="knopf-schlicht"
              onClick={() => void navigator.clipboard.writeText(einladung.einladungslink!)}
            >
              Link kopieren
            </button>
            <button className="knopf-schlicht" onClick={() => setEinladung(null)}>
              Ausblenden
            </button>
          </div>
        </div>
      )}

      {formular && (
        <div className="karte abschnitt">
          <h2>Neuer Mandant</h2>
          <form onSubmit={anlegen}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div className="feld">
                <label htmlFor="kuerzel">Kürzel *</label>
                <input
                  id="kuerzel"
                  type="text"
                  required
                  pattern="[a-z0-9\-]+"
                  title="Nur Kleinbuchstaben, Ziffern und Bindestriche"
                  value={formular.kuerzel}
                  onChange={(e) => setFormular({ ...formular, kuerzel: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="name">Firmenname *</label>
                <input
                  id="name"
                  type="text"
                  required
                  value={formular.name}
                  onChange={(e) => setFormular({ ...formular, name: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="strasse">Straße</label>
                <input
                  id="strasse"
                  type="text"
                  value={formular.strasse}
                  onChange={(e) => setFormular({ ...formular, strasse: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="plz">PLZ</label>
                <input
                  id="plz"
                  type="text"
                  value={formular.plz}
                  onChange={(e) => setFormular({ ...formular, plz: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="ort">Ort</label>
                <input
                  id="ort"
                  type="text"
                  value={formular.ort}
                  onChange={(e) => setFormular({ ...formular, ort: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="betriebsleiter">Betriebsleiter (BOKraft)</label>
                <input
                  id="betriebsleiter"
                  type="text"
                  value={formular.betriebsleiter}
                  onChange={(e) => setFormular({ ...formular, betriebsleiter: e.target.value })}
                />
              </div>
            </div>

            <h3 style={{ marginTop: 18, marginBottom: 12 }}>Ansprechpartner</h3>
            <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem', marginTop: -8, marginBottom: 14 }}>
              Der Ansprechpartner wird Administrator dieses Mandanten und erhält eine Einladung
              per E-Mail. Er vergibt sein Passwort selbst und legt anschließend die weiteren
              Benutzer seines Betriebs an.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div className="feld">
                <label htmlFor="ansprechpartner">Name *</label>
                <input
                  id="ansprechpartner"
                  type="text"
                  required
                  placeholder="Klaus Meier"
                  value={formular.ansprechpartner}
                  onChange={(e) => setFormular({ ...formular, ansprechpartner: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="ansprechpartnerEmail">E-Mail *</label>
                <input
                  id="ansprechpartnerEmail"
                  type="email"
                  required
                  value={formular.ansprechpartnerEmail}
                  onChange={(e) => setFormular({ ...formular, ansprechpartnerEmail: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="ansprechpartnerTelefon">Telefon</label>
                <input
                  id="ansprechpartnerTelefon"
                  type="text"
                  value={formular.ansprechpartnerTelefon}
                  onChange={(e) => setFormular({ ...formular, ansprechpartnerTelefon: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="submit" className="knopf-primaer" disabled={speichert}>
                {speichert ? 'Wird angelegt …' : 'Mandant anlegen'}
              </button>
              <button type="button" className="knopf-schlicht" onClick={() => setFormular(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {bearbeiten && (
        <div className="karte abschnitt">
          <h2>Mandant bearbeiten</h2>
          <form onSubmit={speichern}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div className="feld">
                <label>Kürzel *</label>
                <input
                  type="text"
                  required
                  pattern="[a-z0-9\-]+"
                  value={bearbeiten.kuerzel}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, kuerzel: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Firmenname *</label>
                <input
                  type="text"
                  required
                  value={bearbeiten.name}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, name: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Straße</label>
                <input
                  type="text"
                  value={bearbeiten.strasse ?? ''}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, strasse: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>PLZ</label>
                <input
                  type="text"
                  value={bearbeiten.plz ?? ''}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, plz: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Ort</label>
                <input
                  type="text"
                  value={bearbeiten.ort ?? ''}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, ort: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Betriebsleiter</label>
                <input
                  type="text"
                  value={bearbeiten.betriebsleiter ?? ''}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, betriebsleiter: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Status</label>
                <select
                  value={bearbeiten.aktiv ? 'ja' : 'nein'}
                  onChange={(e) => setBearbeiten({ ...bearbeiten, aktiv: e.target.value === 'ja' })}
                >
                  <option value="ja">aktiv</option>
                  <option value="nein">stillgelegt — Anmeldung gesperrt</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="submit" className="knopf-primaer" disabled={speichert}>
                Speichern
              </button>
              <button type="button" className="knopf-schlicht" onClick={() => setBearbeiten(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : mandanten.length === 0 ? (
          <div className="leer">Noch keine Mandanten angelegt.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Kürzel</th>
                <th>Firma</th>
                <th>Ort</th>
                <th>Zugänge</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {mandanten.map((m) => (
                <tr key={m.id}>
                  <td>
                    <strong>{m.kuerzel}</strong>
                  </td>
                  <td>
                    {m.name}
                    {detail?.mandant.id === m.id && (
                      <div style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginTop: 4 }}>
                        {detail.anzahlKurse} Kurse · {detail.anzahlFahrzeuge} Fahrzeuge ·{' '}
                        {detail.anzahlFahrer} Mitarbeiter
                      </div>
                    )}
                  </td>
                  <td>{[m.plz, m.ort].filter(Boolean).join(' ') || '–'}</td>
                  <td className="zahl">{m.anzahlBenutzer}</td>
                  <td>
                    <span className={`etikett ${m.aktiv ? 'gruen' : 'rot'}`}>
                      {m.aktiv ? 'aktiv' : 'stillgelegt'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="knopf-schlicht"
                      style={{ padding: '5px 12px' }}
                      onClick={() => void zeigeDetail(m.id)}
                    >
                      {detail?.mandant.id === m.id ? 'Bestand ausblenden' : 'Bestand'}
                    </button>{' '}
                    <button
                      className="knopf-schlicht"
                      style={{ padding: '5px 12px' }}
                      onClick={() => {
                        setFormular(null)
                        setBearbeiten(m)
                      }}
                    >
                      Bearbeiten
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
